import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import type { DefaultValues } from "react-hook-form";
import { Button, Form } from "../ui";
import { useTranslation } from "react-i18next";
import { generateFields } from "./fields";
import { BulkEditableTable } from "./so-bulk-form";
import type { BulkFormData } from "./so-bulk-form";
import type { FormStructure } from "~/utils/form-util";

export type SoFormProps<T> = {
  values?: T | T[] | null;
  structure: FormStructure<T>;
  onSubmit: (data: T | BulkFormData<T>) => void;
  onChange?: (data: T | BulkFormData<T>) => void;
  isBulkForm?: boolean;
};

export function formFromStructure<T>(
  structure: FormStructure<T>,
): DefaultValues<Record<keyof T, any>> {
  const form = {} as Record<keyof T, any>;
  (Object.keys(structure) as (keyof T | "virtualFields")[]).forEach((k) => {
    if (k === "virtualFields") return;
    const def = structure[k];
    if (def) {
      form[k as keyof T] = def.default ?? "";
    }
  });

  return form as DefaultValues<Record<keyof T, any>>;
}

export function SoForm<T>(props: SoFormProps<T>) {
  return props.isBulkForm ? (
    <BulkEditableTable
      data={props.values as T[]}
      structure={props.structure}
      onSubmit={props.onSubmit}
    />
  ) : (
    <BaseForm {...props} />
  );
}

function BaseForm<T>({ structure, onSubmit, onChange }: SoFormProps<T>) {
  const { t } = useTranslation();

  const form = useForm({ defaultValues: formFromStructure(structure) });

  useEffect(() => {
    const subscription = form.watch((values) => {
      if (onChange) {
        onChange(values as any);
      }
    });
    return () => subscription.unsubscribe();
  }, [form, onChange]);

  const fields = useMemo(() => {
    return generateFields(form, structure, t);
  }, [form.control, structure, t]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {fields}

        <Button type="submit">{t("common.save")}</Button>
      </form>
    </Form>
  );
}
